/**
 * The shared layout of a number's routing option (NumberRoutingControl, SimpleRoutingEditor): a
 * card with the option's icon, name and one-line description, labeled fields, and one Save/Cancel
 * footer. Every option looks and saves the same way. Same styling rule as the routing editors: no
 * dark: variants, grays no lighter than -600 for text.
 */

import { useState } from 'react';
import { HelpCircle, Loader2, Save } from 'lucide-react';

export const routingInputCls =
  'w-full max-w-md rounded-lg border border-secondary-200 bg-white px-3 py-1.5 text-sm text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 disabled:opacity-60';
/** Swap in for the border of an input that fails validation. */
export const invalidInputCls = '!border-red-400 focus:!ring-red-500/20';

/**
 * "?" with a help bubble on hover, or on tap (focus) for touch screens. Opens below and grows away
 * from the nearer screen edge; capped at 45vw so it fits on a phone either way.
 */
// ponytail: same idea as InfoTip in JobStatusesContent (which opens upward, narrower); extract to
// ui/ if a third copy appears.
export function HelpTip({ text }: { text: string }) {
  const [alignEnd, setAlignEnd] = useState(false);
  const place = (e: React.SyntheticEvent<HTMLElement>) =>
    setAlignEnd(e.currentTarget.getBoundingClientRect().left > window.innerWidth / 2);
  return (
    <span className="relative group/tip inline-flex align-middle" onMouseEnter={place} onFocus={place}>
      <button type="button" className="text-gray-500 hover:text-gray-800 focus:text-gray-800 focus:outline-none" aria-label={text}>
        <HelpCircle className="h-3.5 w-3.5" />
      </button>
      <span
        role="tooltip"
        className={`absolute ${alignEnd ? '-right-2' : '-left-2'} top-full mt-1.5 w-64 max-w-[45vw] whitespace-normal bg-gray-900 text-white text-xs font-normal normal-case tracking-normal leading-snug rounded shadow-lg p-2 z-10 hidden group-hover/tip:block group-focus-within/tip:block pointer-events-none`}
      >
        {text}
      </span>
    </span>
  );
}

/**
 * The settings box under the chosen routing card. It has no title of its own: the card it is joined
 * to names the option, so the caller supplies the border and corners that make the two one shape.
 */
export function RoutingPanel({ className = '', style, children }: {
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  return (
    <div className={`bg-white shadow-sm ${className}`} style={style}>
      <div className="space-y-4 p-3">{children}</div>
    </div>
  );
}

/** One labeled setting inside a panel: label (+ "?"), the control, then a validation error or a hint. */
export function RoutingField({ label, help, hint, error, children }: {
  label: string;
  help?: string;
  hint?: React.ReactNode;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-secondary-600">
        {label}
        {help && <HelpTip text={help} />}
      </p>
      {children}
      {error
        ? <p className="text-xs text-red-700" role="alert">{error}</p>
        : hint && <div className="text-xs text-secondary-600">{hint}</div>}
    </div>
  );
}

/** A warning line inside a panel. */
export function RoutingNote({ children }: { children: React.ReactNode }) {
  return <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">{children}</p>;
}

/** The one footer, left-aligned under the fields: Save, Cancel (while dirty), then an error or "Saved". */
export function PanelActions({ dirty, saving, canSave = true, saved, error, onSave, onCancel }: {
  dirty: boolean;
  saving: boolean;
  /** False keeps Save disabled even while dirty (e.g. nothing picked yet). */
  canSave?: boolean;
  saved?: boolean;
  error?: string;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 pt-1">
      <button
        type="button"
        onClick={onSave}
        disabled={!dirty || !canSave || saving}
        className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
      >
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
        Save
      </button>
      {dirty && (
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="rounded-lg border border-secondary-200 px-3 py-1.5 text-sm text-secondary-800 hover:bg-secondary-50 disabled:opacity-40"
        >
          Cancel
        </button>
      )}
      {error
        ? <p className="text-xs text-red-700">{error}</p>
        : saved && !dirty && <p className="text-xs text-emerald-700">Saved</p>}
    </div>
  );
}
